import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef, useRef, useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import mergeRefs from 'react-merge-refs';
import Text from '../Text/Text.js';
import Icon from '../Icon/Icon.js';
import { Classes as Classes$1 } from '../Popover/Popover.js';
import useWarning from '../../hooks/useWarning.js';
import { CommonClasses } from '../../commonClasses.js';
import { useMenuContext } from './MenuContext.js';

const BASE_CLASS = 'nui-menu-item';
const Classes = {
  TEXT: `${BASE_CLASS}-text`,
  ICON: `${BASE_CLASS}-icon`,
  BASE: BASE_CLASS,
  ACTIVE: `${BASE_CLASS}--active`,
  DANGER: `${BASE_CLASS}--danger`,
  DISABLED: `${BASE_CLASS}--disabled`,
  SELECTED: `${BASE_CLASS}--selected`
};
const DISABLED_PROPS = {
  href: undefined,
  onClick: undefined,
  onMouseDown: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  onMouseMove: undefined,
  tabIndex: -1
};
const menuItemPropTypes = {
  /** add outline to indicate keyboard focus */
  active: PropTypes.bool,
  className: PropTypes.string,
  containerProps: PropTypes.shape({}),
  /** add danger color to text and icon */
  danger: PropTypes.bool,
  /** disable the item */
  disabled: PropTypes.bool,
  large: PropTypes.bool,
  /** element that will be rendered before the text. Must be styled separately */
  leftElement: PropTypes.node,
  /** left icon name. Shorthand for leftIconProps={{ name, title: name }} */
  leftIcon: PropTypes.string,
  /** props for Icon that will be rendered at left side */
  leftIconProps: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string
  }),
  /** custom link component, all rest props will be spread to it */
  LinkComponent: PropTypes.elementType,
  /** ref to inner <a/> element */
  linkRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
  /** element that will be rendered after the text. Must be styled separately */
  rightElement: PropTypes.node,
  /** right icon name. Shorthand for rightIconProps={{ name, title: name }} */
  rightIcon: PropTypes.string,
  /** props for Icon that will be rendered at right side */
  rightIconProps: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string
  }),
  selected: PropTypes.bool,
  /** skip closing popover when clicking on item */
  shouldDismissPopover: PropTypes.bool,
  /** text to show inside the item */
  text: PropTypes.node
};
const getSelectedIconProps = selected => selected ? {
  name: 'check',
  title: 'selected'
} : {
  name: 'empty'
};
const MenuItem = /*#__PURE__*/forwardRef(({
  active,
  className,
  containerProps = {},
  danger,
  disabled,
  large = false,
  leftElement,
  leftIcon,
  leftIconProps,
  LinkComponent = 'a',
  linkRef,
  rightElement,
  rightIcon,
  rightIconProps,
  selected,
  shouldDismissPopover = true,
  text,
  ...linkProps
}, ref) => {
  const containerRef = useRef(null);
  const parentRole = useMenuContext();
  process.env.NODE_ENV !== "production" ? useWarning(parentRole !== 'listbox' && typeof selected === 'boolean', '[MenuItem] Prop "selected" can only be used when Menu has role="listbox".') : void 0;
  const [containerAriaProps, linkAriaProps, selectedIconProps] = useMemo(() => {
    switch (parentRole) {
      case 'menu':
        return [{
          role: 'none'
        }, {
          role: 'menuitem'
        }];
      case 'listbox':
        return [{
          role: 'option',
          'aria-selected': !!selected
        }, {}, getSelectedIconProps(selected)];
      default:
        return [];
    }
  }, [parentRole, selected]);
  const maybeRenderIcon = (element, iconName, iconProps) => {
    if (element) return element;
    if (iconName) return /*#__PURE__*/React__default.createElement(Icon, {
      name: iconName,
      title: iconName
    });
    if (iconProps?.name) return /*#__PURE__*/React__default.createElement(Icon, iconProps);
    return null;
  };
  const disabledProps = disabled ? DISABLED_PROPS : {};
  const linkClassName = cx(BASE_CLASS, {
    [Classes.ACTIVE]: active,
    [CommonClasses.LARGE]: large,
    [Classes.DISABLED]: disabled,
    [Classes.DANGER]: danger,
    [Classes$1.DISMISS]: shouldDismissPopover && !disabled
  }, className);
  const link =
  /*#__PURE__*/
  /* @ts-expect-error types are too complicated here */
  React__default.createElement(LinkComponent, _extends({}, linkAriaProps, linkProps, disabledProps, {
    className: linkClassName,
    ref: linkRef
  }), maybeRenderIcon(leftElement, leftIcon, leftIconProps), /*#__PURE__*/React__default.createElement(Text, {
    className: Classes.TEXT,
    ellipsized: true
  }, text), maybeRenderIcon(rightElement, rightIcon, rightIconProps ?? selectedIconProps));
  return /*#__PURE__*/React__default.createElement("li", _extends({
    ref: mergeRefs([ref, containerRef])
  }, containerAriaProps, containerProps), link);
});
MenuItem.propTypes = menuItemPropTypes;
MenuItem.displayName = 'NuiMenuItem';
var MenuItem$1 = MenuItem;

export { MenuItem$1 as default };
